import { RentalTrailerEvent } from "src/models";

export const RENTAL_DEFECT_OPTIONS = [
  'Brake Connections',
  'Brakes',
  'Coupling Devices',
  'Coupling (King) Pin',
  'Doors',
  'Hitch',
  'Landing Gear',
  'Lights - All',
  'Reflectors/Reflective Tape',
  'Roof',
  'Suspension System',
  'Tarpaulin',
  'Tires',
  'Wheels and Rims',
  'Other',
];

export const RENTAL_TIRE_CONDITION_TYPES = [
  { value: 'rating', label: 'Condition Rating', short: 'Rating' },
  { value: 'depth', label: 'Tread Depth (mm)', short: 'mm' },
  { value: 'percentage', label: 'Tread Percentage', short: '%' },
  { value: 'thirtyseconds', label: 'Tread Depth (32nds ")', short: '/32"' },
];

export const RENTAL_TIRE_RATINGS = ['Good', 'Fair', 'Poor'];

export type RentalUsageField = {
  label: string;
  field: 'miles' | 'fuelLevel' | 'defLevel' | 'strapsQuantity' | 'palletJack';
};

export type RentalTrailerTypeDef = {
  value: string;
  label: string;
  damageCodes: string[];
  // which usage fields the type records; keys must match RentalTrailerEvent fields
  usageFields?: RentalUsageField[];
  equipmentItems?: string[];
  // listed on the Trucks page instead of Trailers; GPS-links to "ELD Truck" devices
  truck?: boolean;
  // tire/brake wheel positions; unset means the trailer layout (8 tire slots / 4 brake axle ends)
  tireSlotLabels?: string[];
  tireSlotRows?: number[][];
  brakeSlotLabels?: string[];
  brakeSlotRows?: number[][];
};

export const RENTAL_USAGE_FIELDS: RentalUsageField[] = [
  { label: 'Miles', field: 'miles' },
  { label: 'Fuel Level', field: 'fuelLevel' },
  { label: 'DEF Level', field: 'defLevel' },
  { label: 'Straps QT', field: 'strapsQuantity' },
  { label: 'Pallet Jack', field: 'palletJack' },
];

const TRUCK_USAGE_FIELDS = RENTAL_USAGE_FIELDS.slice(0, 3);

// 3-axle tractor: single steer wheels, dual wheels on both drive axles;
// mirrors RentalTruckTirePositions / RentalTruckBrakePositions backend-side
const TRUCK_TIRE_SLOT_LABELS = ['L1', 'R1', 'L2O', 'L2I', 'R2I', 'R2O', 'L3O', 'L3I', 'R3I', 'R3O'];
const TRUCK_TIRE_SLOT_ROWS = [
  [0, 1],
  [2, 3, 4, 5],
  [6, 7, 8, 9],
];
const TRUCK_BRAKE_SLOT_LABELS = ['L1', 'R1', 'L2', 'R2', 'L3', 'R3'];
const TRUCK_BRAKE_SLOT_ROWS = [
  [0, 1],
  [2, 3],
  [4, 5],
];

// the 14 numbered damage-condition codes from the UFC Leasing inspection forms.
// APPEND-ONLY: marker numbers on diagrams derive from list position, so
// reordering or inserting would renumber historical events
const DAMAGE_CONDITION_CODES = [
  'Bent',
  'Broken',
  'Cut',
  'Dented',
  'Chipped',
  'Cracked',
  'Gouged',
  'Missing',
  'Scuffed',
  'Stained or Soiled',
  'Punctured',
  'Scratched',
  'Torn',
  'Leaking',
];

// keys and damage codes must stay in sync with RentalTrailerTypes in
// backend/app/models/models_rentals/rental_trailer_type.model.go
export const RENTAL_TRAILER_TYPES: RentalTrailerTypeDef[] = [
  {
    value: 'dryVan',
    label: 'Dry Van',
    damageCodes: DAMAGE_CONDITION_CODES,
  },
  {
    value: 'flatBed',
    label: 'Flat Bed',
    damageCodes: DAMAGE_CONDITION_CODES,
  },
  {
    value: 'boxTruck',
    label: 'Box Truck',
    damageCodes: DAMAGE_CONDITION_CODES,
    usageFields: RENTAL_USAGE_FIELDS,
    equipmentItems: [
      'Drivers Trip Record',
      'Permit Book',
      'Fire Extinguisher',
      'Safety Kit',
      'Radio',
      'Credit Card',
      'Other Equipment',
    ],
  },
  {
    value: 'bobtail',
    label: 'Bobtail Truck',
    damageCodes: DAMAGE_CONDITION_CODES,
    usageFields: TRUCK_USAGE_FIELDS,
    equipmentItems: [
      'Drivers Trip Record',
      'Permit Book',
      'Fire Extinguisher',
      'Safety Kit',
      'Radio',
      'Credit Card',
      'Other Equipment',
    ],
    truck: true,
    tireSlotLabels: TRUCK_TIRE_SLOT_LABELS,
    tireSlotRows: TRUCK_TIRE_SLOT_ROWS,
    brakeSlotLabels: TRUCK_BRAKE_SLOT_LABELS,
    brakeSlotRows: TRUCK_BRAKE_SLOT_ROWS,
  },
];

export const rentalTrailerType = (value?: string) =>
  RENTAL_TRAILER_TYPES.find((trailerType) => trailerType.value === value);

export const isRentalTruckType = (value?: string) => !!rentalTrailerType(value)?.truck;

export const Constants = {
  PRODUCT_NAME: 'Counting Trucks',
  PRODUCT_URL: 'countingtrucks.com',

  RENTAL_TRAILER_EVENT_TYPE_OUT: 0,
  RENTAL_TRAILER_EVENT_TYPE_IN: 1,

  RENTAL_TRAILER_EVENT_STATUS_DRAFT: 0,
  RENTAL_TRAILER_EVENT_STATUS_AWAITING_SIGNATURE: 1,
  RENTAL_TRAILER_EVENT_STATUS_SIGNED: 2,
  RENTAL_TRAILER_EVENT_STATUS_AWAITING_CONFIRMATION: 3,

  RENTAL_STATUS_DRAFT: 0,
  RENTAL_STATUS_INSURANCE_SENT: 1,
  RENTAL_STATUS_INSURANCE_SUBMITTED: 2,
  RENTAL_STATUS_INSURANCE_CONFIRMED: 3,
  RENTAL_STATUS_AGREEMENT_SENT: 4,
  RENTAL_STATUS_AGREEMENT_SUBMITTED: 5,
  RENTAL_STATUS_AGREEMENT_CONFIRMED: 6,
  RENTAL_STATUS_ACTIVE: 7,
  RENTAL_STATUS_RETURNED: 8,
  RENTAL_STATUS_COMPLETED: 9,
  RECURRENCE_TYPE_DAILY: 0,
  RECURRENCE_TYPE_WEEKLY: 1,
  RECURRENCE_TYPE_MONTHLY: 2,
  RECURRENCE_TYPE_YEARLY: 3,

  PAY_RATE_TYPE_BY_GROSS_PCNT: 0,
  PAY_RATE_TYPE_BY_MILE: 1,
  PAY_RATE_TYPE_PER_EXTRA_STOP: 2,
  PAY_RATE_TYPE_PER_WEEK: 3,

  LOAD_STATUS_ACTIVE: 0,
  LOAD_STATUS_DELIVERED: 1,
  LOAD_STATUS_PENDING_PAYMENT: 2,
  LOAD_STATUS_PAID: 3,
  LOAD_STATUS_CANCELLED: 4,

  LOAD_STOP_TYPE_BREAK: 0,
  LOAD_STOP_TYPE_FUEL: 1,

  TRIP_STATUS_NEW: 0,
  TRIP_STATUS_STARTED: 1,
  TRIP_STATUS_FINISHED: 2,
  TRIP_STATUS_ABANDONED: 3,

  STATEMENT_TYPE_DRIVER: 0,
  STATEMENT_TYPE_COMPANY: 1,

  LOAD_PAY_RATE_TYPE_DRIVER: 0,
  LOAD_PAY_RATE_TYPE_TRUCK: 1,

  ATTACHED_FILE_ENTITY_TYPE_LOAD: 'LOAD',
  ATTACHED_FILE_ENTITY_TYPE_UNKNOWN: 'UNKNOWN',
  ATTACHED_FILE_ENTITY_TYPE_MAINTENANCE: 'MAINTENANCE',

  STATEMENT_STATUS_NEW: 0,
  STATEMENT_STATUS_DONE: 1,

  STATEMENT_LINE_TYPE_LOAD: 0,
  STATEMENT_LINE_TYPE_CREDIT: 1,
  STATEMENT_LINE_TYPE_FUEL_CHARGE: 2,
  STATEMENT_LINE_TYPE_TOLL: 3,
  STATEMENT_LINE_TYPE_DEDUCTION: 4,
  STATEMENT_LINE_TYPE_WEEKLY_DRIVER_PAY: 5,
  STATEMENT_LINE_TYPE_DRIVER_STATEMENT: 6,

  TRUCK_DRIVER_FILE_TYPE_MVR: 'MVR',
  TRUCK_DRIVER_FILE_TYPE_CLEARINGHOUSE: 'Clearing House',
  TRUCK_DRIVER_FILE_TYPE_DL: 'CDL',
  TRUCK_DRIVER_FILE_TYPE_MEDICAL: 'Medical',
  TRUCK_DRIVER_FILE_TYPE_DOT: 'DOT',
  TRUCK_DRIVER_FILE_TYPE_REGISTRATION: 'Registration',
  TRUCK_DRIVER_FILE_TYPE_INSURANCE: 'Insurance',
  TRUCK_DRIVER_FILE_TYPE_WDT: 'WDT',

  ACCIDENT_STATUS_OPEN: 0,
  ACCIDENT_STATUS_CLOSED_PAID: 1,
  ACCIDENT_STATUS_CLOSED_NOT_PAID: 2,

  ACCIDENT_PROGRESS_STATUS_WORKING_ON_IT: 0,
  ACCIDENT_PROGRESS_STATUS_IN_PROCESS: 1,
  ACCIDENT_PROGRESS_STATUS_DONE: 2,

  MAINTENANCE_ITEM_QUALIFIER_TYPE_TIME: 0,
  MAINTENANCE_ITEM_QUALIFIER_TYPE_ODOMETER: 1,
  MAINTENANCE_ITEM_EVERY_TYPE_DAY: 1,

  TIMEZONES: [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Anchorage',
  ],

  // Safety Trip Event Types
  SAFETY_TRIP_EVENT_TYPE_START: 0,
  SAFETY_TRIP_EVENT_TYPE_END: 1,

  STATES: [
    "AL", "AK", "AR", "AZ", "CA", "CO", "CT", "DC", "DE",
    "FL", "GA", "HI", "IA", "ID", "IL", "IN", "KS", "KY",
    "LA", "MA", "MD", "ME", "MI", "MN", "MO", "MS", "MT",
    "NC", "ND", "NE", "NH", "NJ", "NM", "NV", "NY", "OH",
    "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT",
    "VA", "VT", "WA", "WI", "WV", "WY",
  ]
};

// only a signed outbound, more recent than any other event for the trailer,
// means the unit is actually out (mirrors the logic on the trailer detail page)
export const isRentalTrailerRentedOut = (trailerId?: number, events?: RentalTrailerEvent[]) => {
  const latestEvent = (events || [])
    .filter((event) => event.trailerId === trailerId)
    .sort((a, b) => (a.eventTime < b.eventTime ? 1 : -1))[0];

  return (
    !!latestEvent &&
    latestEvent.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_OUT &&
    latestEvent.rentalTrailerEventStatus === Constants.RENTAL_TRAILER_EVENT_STATUS_SIGNED
  );
};
